import { useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  Settings2,
  ChevronDown,
  ChevronRight,
  Hotel,
  Car,
  Utensils,
  Compass,
  Package,
  DollarSign,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { OptionGroupModal } from './option-group-modal';
import { OptionModal } from './option-modal';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type Option = {
  id: string;
  name: string;
  description: string | null;
  shortLabel: string | null;
  pricingType: string;
  pricingUnit: string;
  price: number;
  currency: string;
  allocationId: string | null;
  unitsPerBooking: number;
  imageUrl: string | null;
  sortOrder: number;
  isDefault: boolean;
  availableUnits: number | null;
};

type OptionGroup = {
  id: string;
  name: string;
  description: string | null;
  type: string;
  isRequired: boolean;
  minSelections: number;
  maxSelections: number;
  sortOrder: number;
  isActive: boolean;
  options: Option[];
};

interface OptionGroupsSectionProps {
  packageId: string;
  onRefresh?: () => void;
}

const groupTypeIcons: Record<string, React.ElementType> = {
  ACCOMMODATION: Hotel,
  TRANSFER: Car,
  MEAL: Utensils,
  ACTIVITY: Compass,
  OTHER: Package,
};

const groupTypeColors: Record<string, string> = {
  ACCOMMODATION: 'bg-blue-100 text-blue-700',
  TRANSFER: 'bg-purple-100 text-purple-700',
  MEAL: 'bg-orange-100 text-orange-700',
  ACTIVITY: 'bg-green-100 text-green-700',
  OTHER: 'bg-gray-100 text-gray-700',
};

export function OptionGroupsSection({ packageId, onRefresh }: OptionGroupsSectionProps) {
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<OptionGroup | undefined>();
  const [editingOption, setEditingOption] = useState<Option | undefined>();
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const {
    data: groups,
    isLoading,
    refetch,
  } = trpc.packageOption.listGroups.useQuery(packageId);

  const deleteGroupMutation = trpc.packageOption.deleteGroup.useMutation({
    onSuccess: () => {
      showSuccessToast('Option group deleted');
      refetch();
      onRefresh?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to delete option group');
    },
  });

  const deleteOptionMutation = trpc.packageOption.deleteOption.useMutation({
    onSuccess: () => {
      showSuccessToast('Option deleted');
      refetch();
      onRefresh?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to delete option');
    },
  });

  const handleAddGroup = () => {
    setEditingGroup(undefined);
    setIsGroupModalOpen(true);
  };

  const handleEditGroup = (group: OptionGroup) => {
    setEditingGroup(group);
    setIsGroupModalOpen(true);
  };

  const handleDeleteGroup = (groupId: string) => {
    if (confirm('Are you sure you want to delete this option group?')) {
      deleteGroupMutation.mutate(groupId);
    }
  };

  const handleAddOption = (groupId: string) => {
    setSelectedGroupId(groupId);
    setEditingOption(undefined);
    setIsOptionModalOpen(true);
  };

  const handleEditOption = (option: Option, groupId: string) => {
    setSelectedGroupId(groupId);
    setEditingOption(option);
    setIsOptionModalOpen(true);
  };

  const handleDeleteOption = (optionId: string) => {
    if (confirm('Are you sure you want to delete this option?')) {
      deleteOptionMutation.mutate(optionId);
    }
  };

  const handleSuccess = () => {
    refetch();
    onRefresh?.();
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const formatPrice = (price: number, unit: string, currency: string) => {
    if (unit === 'PERCENTAGE') {
      return `${price}%`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          Package Options
        </h2>
        <Button variant="outline" size="sm" onClick={handleAddGroup}>
          <Plus className="mr-2 h-4 w-4" />
          Add Group
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : groups && groups.length > 0 ? (
        <div className="space-y-3">
          {groups.map((group) => {
            const GroupIcon = groupTypeIcons[group.type] || Package;
            const isExpanded = expandedGroups.has(group.id);

            return (
              <Collapsible
                key={group.id}
                open={isExpanded}
                onOpenChange={() => toggleGroup(group.id)}
              >
                <div className="rounded-lg border">
                  {/* Group Header */}
                  <div className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>

                    <div
                      className={`rounded p-1.5 ${groupTypeColors[group.type] || groupTypeColors.OTHER}`}
                    >
                      <GroupIcon className="h-4 w-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{group.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({group.options.length} option{group.options.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {group.isRequired && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            Required
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {group.maxSelections === 1
                            ? 'Single choice'
                            : `Multi-select (max ${group.maxSelections})`}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddOption(group.id);
                      }}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Option
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditGroup(group)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit Group
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeleteGroup(group.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Group
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Options List */}
                  <CollapsibleContent>
                    <div className="border-t">
                      {group.options.length > 0 ? (
                        <div className="divide-y">
                          {group.options.map((option) => (
                            <div
                              key={option.id}
                              className="flex items-center gap-3 p-3 pl-12 hover:bg-muted/20 transition-colors group"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{option.name}</span>
                                  {option.isDefault && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 text-green-600 border-green-300"
                                    >
                                      <CheckCircle className="h-3 w-3 mr-0.5" />
                                      Default
                                    </Badge>
                                  )}
                                </div>
                                {option.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                    {option.description}
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className="flex items-center gap-1 text-sm font-medium">
                                    <DollarSign className="h-3 w-3" />
                                    {formatPrice(option.price, option.pricingUnit, option.currency)}
                                  </div>
                                  <p className="text-[10px] text-muted-foreground">
                                    {option.pricingUnit === 'PER_PERSON'
                                      ? 'per person'
                                      : option.pricingUnit === 'PER_BOOKING'
                                        ? 'per booking'
                                        : 'of base'}
                                  </p>
                                </div>

                                {option.availableUnits !== null && (
                                  <div className="text-right">
                                    <p className="text-xs text-muted-foreground">
                                      {option.availableUnits} avail
                                    </p>
                                  </div>
                                )}

                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => handleEditOption(option, group.id)}
                                    >
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit Option
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-red-600"
                                      onClick={() => handleDeleteOption(option.id)}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete Option
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 pl-12 text-center">
                          <p className="text-sm text-muted-foreground">
                            No options yet.{' '}
                            <button
                              className="text-primary hover:underline"
                              onClick={() => handleAddOption(group.id)}
                            >
                              Add one
                            </button>
                          </p>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Settings2 className="h-8 w-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No option groups</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add option groups to let customers customize their package
          </p>
        </div>
      )}

      {/* Modals */}
      {isGroupModalOpen && (
        <OptionGroupModal
          open={isGroupModalOpen}
          onOpenChange={setIsGroupModalOpen}
          packageId={packageId}
          group={editingGroup}
          onSuccess={handleSuccess}
        />
      )}

      {isOptionModalOpen && selectedGroupId && (
        <OptionModal
          open={isOptionModalOpen}
          onOpenChange={setIsOptionModalOpen}
          groupId={selectedGroupId}
          option={editingOption}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
